import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake, faMagicWandSparkles, faMagnifyingGlassChart,
  faMoneyBill1Wave, faPeopleGroup, faRocket, faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../button";
import { useStaticQuery, graphql } from "gatsby";
import { cn, mapEdgesToNodes } from "../../../lib/helpers";
import PortableText from "../../portableText";
import * as SampleCompany from "../../../../static/data/play-finder.json";
import TextEffect from "gpt-type-effect";
import { ImageBuilder } from "../../global/image-builder";
import { SiteContext } from "../../global/site-context";
import { SectionWrapper } from "../wrapper";
import { GetStarted } from "./get-started-banner";
import { Report } from "./play-finder-report";
import { Distraction } from "./play-finder-distract";
import LZString from 'lz-string';

const TESTING = false;
const API_URL = "/.netlify/functions/play-finder";
const SKIP_FETCH = TESTING;
const SKIP_DELAY = false;
const MAX_LOOKUPS = 3;
const FAKE_DELAY = 5000;
const THINKING_STEP = 1;
const RESULTS_STEP = 2;
export const LLM_MODELS = "Gemini Flash 2.0 + OpenAI O4 Mini";
const LZ_PREFIX = 'lz-';

export const query = graphql`
  query PlayFinderQuery {
    playbooks: allSanityPlaybook(
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          ...cardNodePlaybook
        }
      }
    }
    signals: allSanitySignal(
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          ...cardNodeSignal
        }
      }
    }
  }
`;

const validateDomain = (domain) => {
  const regex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
  return regex.test(domain);
}

export const decompress = (data) => {
  if (data.startsWith(LZ_PREFIX)) {
    const compressed = data.substring(LZ_PREFIX.length);
    const decompressed = LZString.decompressFromBase64(compressed);

    console.log(compressed)
    console.log(decompressed)

    return JSON.parse(decompressed);
  }
  return false;
}

export const compress = (data) => {
  const compressed = `${LZ_PREFIX}${LZString.compressToBase64(JSON.stringify(data))}`;

  return compressed;
}

export const PlayFinder = ({ section, setBrowserTitle }) => {
  const site = useContext(SiteContext);
  const storage = site.metrics.analytics.storage;

  const [messages, setMessages] = useState([]);
  const [domain, setDomain] = useState("");
  const [inputVal, setInputVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(false);
  const [archive, setArchive] = useState([]);
  const [step, setStep] = useState(0);

  const input = useRef(null);
  const results = useStaticQuery(query);
  const playbooks = mapEdgesToNodes(results.playbooks || {});
  const signals = mapEdgesToNodes(results.signals || {});

  const isRoomie = !!storage.getItem("roomie");

  const doSetStep = useCallback((step) => {
    setStep(step);
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  /**
   * Refresh prompt suggestions in AirOps...
   */
  // console.log(JSON.stringify(
  //   playbooks.concat(signals)
  //     .map(n => ({
  //       title: n.title,
  //       summary: n._rawExcerpt[0].children[0].text,
  //       _id: n._id,
  //     }))
  // ));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const extracted = inputVal.match(/^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/im);
    const freshDomain = extracted?.[1] || inputVal;

    if (inputVal && validateDomain(freshDomain)) {
      if (!site.isAdmin && !isRoomie && (storage.getItem("play-finder-lookups") > MAX_LOOKUPS)) {
        setMessages(messages => messages.concat([
          `You reached the max lookups. Please try again later.`
        ]));
      } else {
        lookupDomain(freshDomain);
        setDomain(freshDomain);
        setInputVal("");
        setLoading(true);
        doSetStep(THINKING_STEP);
        setMessages([]);
      }
    } else {
      setMessages(messages => messages.concat([`Please enter your domain`]));
    }
  }, [inputVal]);

  // load data from URL param or storage
  useLayoutEffect(() => {
    if (!window) return;

    const param = new URLSearchParams(window.location.search).get("c");
    const lzParam = decodeURIComponent(param);
    const data = (param && lzParam.startsWith(LZ_PREFIX))
      ? decompress(lzParam)
      : storage.getItem("play-finder");

    if (data) {
      console.debug("Found data in storage or param", data);
      doSetCompany(data);
      setDomain(data.domain);
      setLoading(false);
      if (param) {
        // remove the param if there is one
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
  }, []);

  useLayoutEffect(() => {
    // multiple lookups
    const archive = storage.getItem("play-finder-archive");
    if (archive && archive.length > 0) {
      setArchive(archive);
    }

    // roomie status user
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("roomie")) {
      storage.setItem("roomie", true);
    }
  }, [company]);

  const doSetCompany = useCallback((company) => {
    setCompany(company);
    doSetStep(RESULTS_STEP);
    setMessages([]);
    storage.setItem("play-finder", company);
    console.debug("Company set", company);
    setBrowserTitle(`${company.name} AI plays with Common Room`);

    // stash if accetable
    if (company?.conclusions?.isAppropriate?.value || company?.conclusions?.isTAM?.value) {
      const archived = storage.getItem("play-finder-archive") || [];
      storage.setItem("play-finder-archive", [].concat([company]).concat(
        archived.filter(c => c.domain !== company.domain) || []
      ));
    }
  }, []);

  const reset = useCallback(() => {
    const param = new URLSearchParams(window.location.search).get("c");
    setCompany(null);
    storage.removeItem("play-finder");
    setDomain("");
    setMessages([]);
    setLoading(false);
    setInputVal("");
    setBrowserTitle(site.doc.title);
    if (window && param) {
      // remove the param if there is one
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // input.current.focus();
    doSetStep(0);
  }, []);

  const lookupDomain = useCallback(async (domain) => {
    // Add to the lookup count
    storage.setItem("play-finder-lookups", (storage.getItem("play-finder-lookups") || 0) + 1);
    if (SKIP_FETCH) {
      // faking data fetch
      setLoading(false);
      if (SKIP_DELAY) {
        doSetCompany(SampleCompany);
      } else {
        setTimeout(() => {
          // progressMessages(domain);
          doSetCompany(SampleCompany);
        }, FAKE_DELAY);
      }
    } else {
      // fetching company data
      const sendData = { domain, token: site.buildToken };
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: isRoomie ? { ...sendData, roomie: isRoomie } : sendData,
        }),
      });
      const data = await res.json();

      if (!res.error && res.ok) {
        if (data?.result?.name) {
          // plays picked
          setLoading(false);
          doSetCompany(data.result);
        } else {
          setMessages(messages => messages.concat([`Problem finding details for ${domain}.`]));
          console.debug("Data", data);
          setLoading(false);
          doSetStep(0);
        }
      } else {
        setMessages(messages => messages.concat(data?.error || "Error fetching data."));
        setLoading(false);
        doSetStep(0);
      }
    }
  }, []);

  return (
    <div className="pb-24 mb-40">
      {step === 0 ? (
        <div className="relative md:pt-8 pb-24" style={{ minHeight: 600 }}>
          <div className="text-center mb-10 max-w-screen-sm mx-auto px-4 md:px-0 relative">
            <span
              className="md:absolute md:top-12 pt-1 md:right-16 text-lemon opacity-50 transition-colors duration-200 text-sm font-semibold tracking-wide uppercase">
              Beta
            </span>
            <PortableText blocks={section._rawSummary} />
          </div>
          <div className="max-w-3/4 md:max-w-screen-sm mx-auto">
            <DomainForm
              inputVal={inputVal}
              setInputVal={setInputVal}
              handleSubmit={handleSubmit}
              loading={loading}
              input={input}
            />
            {messages.length > 0 && (
              <ul className="text-white max-w-screen-sm mx-auto bg-dark-10 rounded-b-xl p-6 pb-3" role="list">
                {messages.map((msg, i) => (
                  <li key={i} className="border-b border-light-5 pt-2 pb-1 mb-2 first:border-t-0">
                    {/* <FontAwesomeIcon icon={faCommenting} className="text-auxiliary" /> */}
                    <TextEffect text={msg} notDisplayCaretAfterFinishes={true} minTypingDelay={300} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-center text-light-30 text-sm leading-normal my-10 mx-6 sm:mx-2 sm:w-full hover:text-light-60 transition-colors duration-200">
            Results may vary, powered by <span className="font-medium">{LLM_MODELS}.</span>
          </p>
          {archive.length > 0 && (
            <div className="max-w-screen-sm mx-auto rounded-lg text-light-60 flex justify-center">
              <ul className={cn(
                archive.length < 3 ? "flex flex-col md:flex-row gap-2" : "grid md:grid-cols-2 gap-4",
              )}
              >
                {archive.map((company, i) => (
                  <li key={i}>
                    <a
                      href={`/plays/?c=${encodeURIComponent(compress(company))}`}
                      className="pl-3 hover:text-link flex items-center gap-1"
                    >
                      <div
                        className="bg-white p-1 rounded-md flex justify-center items-center mr-1"
                        style={{ minHeight: 36, minWidth: 36 }}
                      >
                        <ImageBuilder
                          src={`https://logo.clearbit.com/${company.domain}`}
                          alt={company.name}
                          width={28}
                          style={{
                            maxHeight: 28, maxWidth: 28,
                            minHeight: 28, minWidth: 28,
                          }}
                        />
                      </div>
                      {company.domain}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <FloatyIcon icon={faMagnifyingGlassChart} className="hidden lg:flex top-0 right-20" />
          <FloatyIcon icon={faMoneyBill1Wave} className="hidden lg:flex bottom-0 right-20 delay-1000" />
          <FloatyIcon icon={faRocket} className="hidden lg:flex top-0 left-20 delay-1500" />
          <FloatyIcon icon={faHandshake} className="hidden lg:flex bottom-0 left-20 delay-2000" />
          <FloatyIcon icon={faThumbsUp} className="hidden lg:flex top-1/2 right-0 -mt-10 delay-2500" />
          <FloatyIcon icon={faPeopleGroup} className="hidden lg:flex top-1/2 left-0 -mt-10 delay-500" />
        </div>
      ) : step === THINKING_STEP ? (
        <Distraction domain={domain} />
      ) : (step === RESULTS_STEP) ? (
        <>
          <Report content={playbooks.concat(signals)} company={company} reset={reset} />
          <div className="overflow-hidden rounded-2xl">
            <SectionWrapper background="mint" layout="centered" container="thin">
              <GetStarted />
            </SectionWrapper>
          </div>
        </>
      ) : null}
    </div>
  );
}

const FloatyIcon = ({ icon, className }) => (
  <div className={cn(
    "text-link opacity-20 animate-breath absolute border border-link rounded-full bg-dark-30 h-12 w-12 flex items-center justify-center",
    className
  )}
  >
    <FontAwesomeIcon icon={icon} size="xl" />
  </div>
);

const DomainForm = ({ inputVal, setInputVal, handleSubmit, loading, input }) => {
  const site = useContext(SiteContext);

  return (
    <form className="flex gap-2 flex-col sm:flex-row border-4 border-link p-1 rounded-xl bg-dark-30 shadow-lg" onSubmit={handleSubmit}>
      <input
        type="text"
        placeholder={loading ? "Processing..." : site.isNotMobile ? "yourdomain.com" : "mydomain.com"}
        className="bg-transparent py-4 sm:py-2.5 px-4 text-white flex-grow outline-none focus:ring-none w-full text-center sm:text-left"
        autoFocus={!!site.isNotMobile}
        ref={input}
        onClick={() => {
          if (input.current) {
            input.current.placeholder = "";
          }
        }}
        onChange={(e) => {
          setInputVal(e.target.value);
        }}
        value={inputVal || ""}
        disabled={loading}
      // defaultValue={TESTING ? "testing.io" : null}
      />
      <Button
        text="Show me plays"
        icon={
          <FontAwesomeIcon icon={faMagicWandSparkles} />
        }
        onClick={handleSubmit}
        deactivated={loading}
        className="whitespace-nowrap"
        metrics={{
          action: "play-finder",
          label: "show-me-plays",
        }}
      />
    </form>
  )
}
